import { useEffect } from "react";
import { Link } from "react-router-dom";
import SiteFooter from "@/components/pivotbox/SiteFooter";
import SiteHeader from "@/components/pivotbox/SiteHeader";
import StageBreadcrumb from "@/components/pivotbox/StageBreadcrumb";
import UnlockDiagnosticCTA from "@/components/pivotbox/UnlockDiagnosticCTA";
import "../S1Diagnostic.css";
import "../sdiag/SDiagnosticExtras.css";
import "./CanvasLanding.css";

/**
 * CanvasLandingPage — shared layout for /strategyblueprint (S3) and /founderoperatingsystem (S4).
 * Driven entirely by the data prop (see canvasLandingData.js).
 */
export default function CanvasLandingPage({ data }) {
  useEffect(() => {
    document.title = `${data.pageTitle} | Pivotbox`;
    window.scrollTo(0, 0);
  }, [data]);

  const isS3 = data.stageCode === "S3";
  const isS4 = data.stageCode === "S4";

  return (
    <div className="canvas-landing" data-testid={data.testId}>
      {/* HEADER — global site header */}
      <SiteHeader testIdPrefix={data.testId} />

      <main className="canvas-landing__main">
        {/* HERO */}
        <section className="cl-hero pivotbox-container" data-testid={`${data.testId}-hero`}>
          <div className="cl-hero-eyebrow">
            <span className="cl-hero-eyebrow-line" aria-hidden="true" />
            <span className="cl-hero-eyebrow-text">{data.hero.eyebrow}</span>
          </div>
          <h1 className="cl-hero-headline" data-testid={`${data.testId}-headline`}>
            {data.hero.headlineMain}<br />
            <em>{data.hero.headlineItalic}</em>
          </h1>
          <p className="cl-hero-sub">{data.hero.sub}</p>
          <div className="cl-hero-actions">
            {data.hero.primaryCta.to === "/diagnostic" ? (
              <UnlockDiagnosticCTA
                className="cl-btn-primary"
                buttonLabel={`${data.hero.primaryCta.label} \u00A0\u2192`}
                testId={`${data.testId}-hero-cta`}
              />
            ) : (
              <Link to={data.hero.primaryCta.to} className="cl-btn-primary" data-testid={`${data.testId}-hero-cta`}>
                {data.hero.primaryCta.label} &nbsp;&rarr;
              </Link>
            )}
            <span className="cl-hero-tag">
              {data.hero.tag.prefix} &nbsp;&middot;&nbsp; <strong>{data.hero.tag.value}</strong>
            </span>
          </div>

          {/* Stage progression breadcrumb (S0 → S4) */}
          <StageBreadcrumb currentStage={data.stageCode} testId={`${data.testId}-breadcrumb`} />

          <hr className="cl-hero-divider" />
        </section>

        {/* SESSION BAND */}
        <section className="cl-session-band pivotbox-container">
          <div className="cl-session-band-inner">
            <div className="cl-session-band-copy">
              <div className="cl-session-band-label">{data.sessionBand.label}</div>
              <div className="cl-session-band-text">{data.sessionBand.text}</div>
            </div>
            <div className="cl-session-band-aside">
              {data.sessionBand.stats.map((s, i) => (
                <div className="cl-session-stat" key={i}>
                  <div className="cl-session-stat-num">{s.num}</div>
                  <div className="cl-session-stat-label">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* YOU BAND */}
        <section className="cl-you-band pivotbox-container">
          <div className="cl-you-band-inner">
            <div>
              <div className="cl-you-band-label">{data.youBand.label}</div>
              <p className="cl-you-band-text">
                {data.youBand.text.plain}<em>{data.youBand.text.italic}</em>{data.youBand.text.post}
              </p>
            </div>
            <div className="cl-you-band-aside">
              {data.youBand.items.map((it, i) => (
                <div className="cl-you-band-aside-item" key={i}>
                  <div className="cl-you-band-aside-dot" aria-hidden="true" />
                  <p className="cl-you-band-aside-text"><strong>{it.strong}</strong>{it.rest}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WHY */}
        <section className="cl-section pivotbox-container" id="why">
          <div className="cl-section-label">
            <span className="cl-section-label-line" aria-hidden="true" />
            <span className="cl-section-label-text">{data.why.label}</span>
          </div>
          <h2 className="cl-section-heading">
            {data.why.heading.map((line, i) => (
              <span key={i}>{line}{i < data.why.heading.length - 1 && <br />}</span>
            ))}
          </h2>
          <div className="cl-why-layout">
            <div>
              {data.why.paragraphs.map((p, i) => (<p className="cl-why-para" key={i}>{p}</p>))}
            </div>
            <div>
              <div className="cl-why-callout-inner">
                <div className="cl-why-callout-icon" aria-hidden="true">↳</div>
                <p className="cl-why-callout-text">{data.why.callout}</p>
              </div>
            </div>
          </div>
        </section>

        {/* CANVASES */}
        <section className="cl-section pivotbox-container" id="canvases">
          <div className="cl-section-label">
            <span className="cl-section-label-line" aria-hidden="true" />
            <span className="cl-section-label-text">{data.canvases.label}</span>
          </div>
          <h2 className="cl-section-heading">{data.canvases.heading}</h2>
          <p className="cl-section-intro">{data.canvases.intro}</p>
          <div className="cl-canvas-grid">
            {data.canvases.items.map((c) => (
              <div className={`cl-canvas-card ${c.checkpoint ? "is-checkpoint" : ""} ${c.highlight ? "is-highlight" : ""}`} key={c.num}>
                <div className="cl-canvas-num">{c.num}</div>
                <div className="cl-canvas-tag">{c.tag}</div>
                <h3 className="cl-canvas-title">{c.title}</h3>
                <p className="cl-canvas-question">{c.question}</p>
                <p className="cl-canvas-desc">{c.desc}</p>
                <div className="cl-canvas-bullets">
                  {c.bullets.map((b, i) => (
                    <div className="cl-canvas-bullet" key={i}>
                      <span className="cl-canvas-bullet-dot" aria-hidden="true" />
                      <span>{b}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* SHIFT (What you leave with) */}
        <div className="cl-shift-bg">
          <section className="cl-section pivotbox-container">
            <div className="cl-section-label">
              <span className="cl-section-label-line" aria-hidden="true" />
              <span className="cl-section-label-text">{data.shift.label}</span>
            </div>
            <h2 className="cl-section-heading">{data.shift.heading}</h2>
            <p className="cl-section-intro">{data.shift.intro}</p>
            <div className="cl-shift-grid">
              {data.shift.cards.map((sc, i) => (
                <div className="cl-shift-card" key={i}>
                  <div className="cl-shift-card-before">Before</div>
                  <div className="cl-shift-card-quote">{sc.before}</div>
                  <div className="cl-shift-card-arrow" aria-hidden="true">→</div>
                  <div className="cl-shift-card-after">{sc.after}</div>
                  <p className="cl-shift-card-desc">{sc.desc}</p>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* FRAMEWORK */}
        <div className="cl-framework-bg">
          <section className="cl-section pivotbox-container" id="framework">
            <div className="cl-section-label">
              <span className="cl-section-label-line" aria-hidden="true" />
              <span className="cl-section-label-text">{data.framework.label}</span>
            </div>
            <h2 className="cl-section-heading">
              {data.framework.heading.map((line, i) => (
                <span key={i}>{line}{i < data.framework.heading.length - 1 && <br />}</span>
              ))}
            </h2>
            <p className="cl-section-intro">{data.framework.intro}</p>
            <div className="cl-fw-table">
              <div className="cl-fw-row cl-fw-header">
                <div className="cl-fw-col-stage">Stage</div>
                <div className="cl-fw-col-name">Assessment</div>
                <div className="cl-fw-col-q">The question it answers</div>
              </div>
              {[
                { stage: "S0", name: "Founder Signal Test", q: "Are you wired to build?", completed: true },
                { stage: "S1", name: "Founder Reality Check", q: "Is your life positioned to support this transition?", completed: true },
                { stage: "S2", name: "Founder Diagnostic", q: "Who are you as a founder — how you build, hold up, and execute.", group: true },
                { stage: "S2.1", name: "Founder Archetype", q: "How are you naturally wired to build?", completed: true, sub: true },
                { stage: "S2.2", name: "Founder Durability", q: "How do you respond to failure and sustained uncertainty?", completed: true, sub: true },
                { stage: "S2.3", name: "Founder Capability & Execution Profile", q: "Do you have the capabilities to execute your business model?", completed: true, sub: true },
                { stage: "S3", name: "Founder Strategy Blueprint", q: "Is the venture concept, model, and market validated enough to commit?", completed: isS4, current: isS3, dim: isS4 },
                { stage: "S4", name: "Founder Operating System", q: "How do you go from first revenue to a business that scales?", current: isS4, group: isS3, last: true },
              ].map((r, i) => {
                const cls = [
                  "cl-fw-row",
                  r.group ? "cl-fw-group" : "",
                  r.sub ? "cl-fw-sub" : "",
                  r.current ? "cl-fw-current" : "",
                  r.dim ? "cl-fw-dim" : "",
                  r.last ? "cl-fw-last" : "",
                ].filter(Boolean).join(" ");
                return (
                  <div className={cls} key={i}>
                    <div className="cl-fw-col-stage"><span className={r.sub ? "cl-fw-sub-stage" : r.group ? "cl-fw-group-stage" : "cl-fw-stage"}>{r.stage}</span></div>
                    <div className="cl-fw-col-name">
                      <strong>{r.name}</strong>
                      {r.completed && <span className="cl-fw-completed">✓ Completed</span>}
                      {r.current && <span className="cl-fw-you-are">← You are here</span>}
                    </div>
                    <div className="cl-fw-col-q">{r.q}</div>
                  </div>
                );
              })}
            </div>
          </section>
        </div>

        {/* REFUND — same as S2.x landing pages */}
        <section className="s1d-refund cl-refund-band" data-testid={`${data.testId}-refund`}>
          <div className="s1d-container">
            <div className="s1d-refund__card">
              <div className="s1d-refund__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2 4 5v6c0 5 3.5 9.5 8 11 4.5-1.5 8-6 8-11V5l-8-3Z" /><path d="m9 12 2 2 4-4" /></svg>
              </div>
              <div className="s1d-refund__body">
                <h3 className="s1d-refund__heading">If it does not deliver, it does not cost.</h3>
                <p className="s1d-refund__sub">Take the full Pivotbox diagnostic. If it does not give you the clarity we promise, we will refund you in full. No questions, no forms, no friction.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="cl-cta-section" data-testid={`${data.testId}-cta-section`}>
          <div className="pivotbox-container cl-cta-inner">
            <div className="cl-cta-copy">
              <span className="cl-cta-logo-wrap">
                <img src="/pivotbox-p-logo.png" alt="Pivotbox" className="cl-cta-logo" />
                <span className="cl-cta-logo-text">Pivotbox</span>
              </span>
              <h2 className="cl-cta-heading">
                {data.cta.heading.map((line, i) => (
                  <span key={i}>{line}{i < data.cta.heading.length - 1 && <br />}</span>
                ))}
              </h2>
              <p className="cl-cta-sub">{data.cta.sub}</p>
            </div>
            <div className="cl-cta-right">
              <div className="cl-cta-session-block">
                <div className="cl-cta-session-label">{data.cta.label}</div>
                {data.cta.price ? (
                  <div className="cl-cta-prices">
                    <span className="cl-cta-price-strike">&#8377;{data.cta.price.strike}</span>
                    <span className="cl-cta-price-current">&#8377;{data.cta.price.current}</span>
                  </div>
                ) : (
                  <div className="cl-cta-session-value">{data.cta.stageValue}</div>
                )}
                <div className="cl-cta-session-note">{data.cta.note}</div>
              </div>
              {data.cta.button.to === "/diagnostic" ? (
                <UnlockDiagnosticCTA
                  className="cl-btn-white"
                  buttonLabel={`${data.cta.button.label} \u00A0\u2192`}
                  testId={`${data.testId}-cta-btn`}
                />
              ) : (
                <Link to={data.cta.button.to} className="cl-btn-white" data-testid={`${data.testId}-cta-btn`}>
                  {data.cta.button.label} &nbsp;&rarr;
                </Link>
              )}
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
